import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useBazaarVoice from 'hooks/useBazaarVoice';
import BVHelmet from './BVHelmet';

/**
 * BazaarVoice renders reviews inside a Shadow DOM (<template shadowrootmode="open">).
 * External CSS cannot pierce the shadow boundary, so we inject styles directly
 * into the shadow root via JS polling. This is the only way to override BV styles.
 */
const BV_SHADOW_DOM_STYLES = `
  /* Fix secondary ratings overflow (Quality/Value of Product) */
  /*
   * DOM structure (from .sc-16dr7i1-27 down to .bv_content_label_row):
   * .sc-16dr7i1-27 (flex row - THIS IS THE TARGET)
   *   ├── .sc-16dr7i1-10 (review content)
   *   └── .sc-16dr7i1-12 (secondary ratings wrapper)
   *        └── .di1qf-0
   *             └── .di1qf-1
   *                  └── .di1qf-2
   *                       └── .bv_content_label_row
   *
   * We use :has() to climb up 5 levels from .bv_content_label_row
   */
  #reviews_container [id^="bv-review-"] div:has(> div > div > div > div > .bv_content_label_row) {
    display: flex !important;
    flex-direction: column !important;
  }

  /* Ensure the secondary ratings block respects container width */
  #reviews_container div:has(> .bv_content_label_row) {
    max-width: 100% !important;
    width: 100% !important;
  }

  /* Remove the empty gray background box that remains after reflow */
  #reviews_container [id^="bv-review-"] div:has(> div > div > div > div > .bv_content_label_row)::before,
  #reviews_container [id^="bv-review-"] div:has(> div > div > div > div > .bv_content_label_row)::after {
    display: none !important;
  }

  /* The secondary ratings wrapper likely has a background - remove it since content moved */
  #reviews_container div:has(> div > div > .bv_content_label_row) {
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
  }

  /* Keep label text on one line */
  #reviews_container .bv_content_label_row {
    white-space: nowrap !important;
  }
`;
const BVReviews = t0 => {
  const $ = _c(6);
  const {
    productId
  } = t0;
  useBazaarVoice();
  const containerRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== productId) {
    t1 = () => {
      if (!productId) {
        return;
      }
      const interval = setInterval(() => {
        const {
          current
        } = containerRef;
        if (!current) {
          return;
        }
        const {
          shadowRoot
        } = current;
        if (shadowRoot && !shadowRoot.querySelector("#bv-custom-styles")) {
          const styleEl = document.createElement("style");
          styleEl.id = "bv-custom-styles";
          styleEl.textContent = BV_SHADOW_DOM_STYLES;
          shadowRoot.prepend(styleEl);
          clearInterval(interval);
        }
      }, 500);
      return () => clearInterval(interval);
    };
    t2 = [productId];
    $[0] = productId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (!productId) {
    return null;
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <BVHelmet />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== productId) {
    t4 = <>{t3}<div ref={containerRef} data-bv-show="reviews" data-bv-product-id={productId} /></>;
    $[4] = productId;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
};
BVReviews.propTypes = {
  productId: PropTypes.string
};
export default BVReviews;